import { Flex, Text, VStack } from '@chakra-ui/react'
import { ResultStatus, VisitActionFlag } from '../../../types/script'
import type { CanvasResultSettingsFormState } from '../../../types/script'
import { isLocked, isOn, toggle, type OutcomeItem } from '../../../lib/outcomeSettings'
import { OutcomeFrame, type FrameGroup } from './OutcomeFrame'

const R = (value: ResultStatus): OutcomeItem => ({ kind: 'result', value })
const F = (value: VisitActionFlag): OutcomeItem => ({ kind: 'flag', value })

/** [Spec §5.3] Frame → groups → [canvasser copy, item]. Labels are the door screens' own button text. */
const FRAMES: { title: string; groups: { label?: string; rows: [string, OutcomeItem][] }[] }[] = [
  { title: 'What happened?', groups: [
    { rows: [['Someone answered', R(ResultStatus.Completed)], ['Nobody home', R(ResultStatus.NoAnswer)],
             ["Couldn't knock", R(ResultStatus.Inaccessible)]] },
    { label: 'Nobody home offers', rows: [['Left literature', F(VisitActionFlag.LeftLiterature)],
             ['Come back later', F(VisitActionFlag.ComeBackLater)]] },
  ] },
  { title: 'Who answered?', groups: [
    { rows: [['Start conversation', R(ResultStatus.Completed)], ['Refused at door', R(ResultStatus.Refused)],
             ['Language barrier', R(ResultStatus.LanguageBarrier)]] },
    { label: 'Per-person menu (⋯)', rows: [['Moved', R(ResultStatus.Moved)], ['Deceased', R(ResultStatus.Deceased)]] },
  ] },
  { title: 'How did it end?', groups: [
    { rows: [['Finished', R(ResultStatus.Completed)], ['They declined partway', R(ResultStatus.Refused)],
             ['Busy, come back', R(ResultStatus.Busy)], ['Language barrier', R(ResultStatus.LanguageBarrier)]] },
    { label: 'Follow-ups', rows: [['Left literature', F(VisitActionFlag.LeftLiterature)],
             ['Come back later', F(VisitActionFlag.ComeBackLater)], ['Schedule callback', F(VisitActionFlag.ScheduleCallback)]] },
  ] },
  { title: 'Everyone else here?', groups: [
    { rows: [['Not home', R(ResultStatus.NoAnswer)], ['Moved', R(ResultStatus.Moved)],
             ['Deceased', R(ResultStatus.Deceased)], ['Refused', R(ResultStatus.Refused)]] },
  ] },
]

/** [Spec §5.3, D9] The Door outcome settings, previewed as the four door screens. Self-contained (3C can host it). */
export function OutcomeSettingsEditor({ value, onChange }: {
  value: CanvasResultSettingsFormState; onChange: (next: CanvasResultSettingsFormState) => void
}) {
  return (
    <VStack align="stretch" gap={3}>
      <Flex gap={4} wrap="wrap" align="flex-start">
        {FRAMES.map(f => (
          <OutcomeFrame key={f.title} title={f.title} groups={f.groups.map((g): FrameGroup => ({
            label: g.label,
            rows: g.rows.map(([label, item]) => ({
              label, locked: isLocked(item), on: isOn(value, item), onToggle: () => onChange(toggle(value, item)),
            })),
          }))} />
        ))}
      </Flex>
      <Text fontSize="sm" color="fg.muted">Contact walks and worksite stops use these same settings.</Text>
    </VStack>
  )
}
